import { useMemo } from "react";
import { useSelector } from "react-redux";
import { OrderSummary } from "@app/domain";
import { useDriverLocation } from "@app/data";
import { useDocumentVisibility } from "@app/shared/hooks/useDocumentVisibility";
import { RootState } from "@app/store";
import { t } from "@app/utils/translation";
import { GoogleMapAdapter, OsmMapAdapter } from "./adapters";
import { useMarkerEta, useOrderMapMarkers } from "./hooks";
import { selectCameraMarkers } from "./utils/computeBounds";
import { MapProviderConfig } from "./MapView.types";
import styles from "./MapView.module.css";

export type MapViewProps = {
  order: OrderSummary;
  /** See `isDropoffTracking` in HomePage.tsx — only affects whether the ETA
   * overlay is eligible once the order is picked up, and which marker the
   * initial camera fit/center focuses on (see `selectCameraMarkers`). */
  isDropoffTracking?: boolean;
  /**
   * Marker id (Pickup or a specific Dropoff) the map's camera should pan/fit
   * to right now — set only by `DesktopTrackingShell` while a specific Stop
   * Details page is open on desktop. `null`/undefined everywhere else, which
   * leaves the usual fit-on-load behavior as the map's only camera driver.
   */
  focusMarkerId?: string | null;
  /**
   * Sandbox-only escape hatch (`?map=osm|google` — see HomePage.tsx) for
   * trying a different provider against the same fixture without editing
   * mock data. In production this prop simply wouldn't be passed, and
   * `order.mapProvider.kind` alone decides — that's the entire "switch
   * providers" surface the rest of the app depends on.
   */
  providerOverride?: MapProviderConfig["kind"];
  className?: string;
};

const DRIVER_TRACKABLE_STATUS_CODES = ["Started", "Arrived", "PickedUp"];

/**
 * The Map feature's one public component. Everything upstream (HomePage)
 * needs to know is "here's the order, here's whether we're in dropoff-
 * tracking mode" — provider selection, marker derivation, driver polling,
 * and ETA placement are all owned here and never leak out as separate
 * concerns HomePage has to wire up itself.
 */
export const MapView = ({
  order,
  isDropoffTracking = false,
  focusMarkerId = null,
  providerOverride,
  className,
}: MapViewProps) => {
  const userActive = useDocumentVisibility();
  const language = useSelector((state: RootState) => state.setting.language);

  // Exactly the brief's driver-visibility condition:
  // (status === "Started" || "Arrived" || "PickedUp") && userActive — plus
  // non-Ondemand orders never show a driver at all (they show their last
  // event in the bottom panel instead — see BottomPanel).
  const driverTrackingEnabled =
    order.kind === "ondemand" &&
    !!order.driver &&
    DRIVER_TRACKABLE_STATUS_CODES.includes(order.status.code) &&
    userActive;

  const driverLocation = useDriverLocation(order.id, driverTrackingEnabled);
  const markers = useOrderMapMarkers(order, driverLocation, isDropoffTracking);
  const cameraMarkers = useMemo(() => selectCameraMarkers(markers, isDropoffTracking), [markers, isDropoffTracking]);
  const etaOverlay = useMarkerEta(order, isDropoffTracking);

  const providerKind = providerOverride ?? order.mapProvider.kind;

  if (providerKind === "google") {
    const apiKey = order.mapProvider.kind === "google" ? order.mapProvider.apiKey : "";
    if (!apiKey) {
      return (
        <div className={className ?? styles.placeholder}>
          {t("GoogleMapsApiKeyMissing", language)}
        </div>
      );
    }
    return (
      <GoogleMapAdapter
        markers={markers}
        cameraMarkers={cameraMarkers}
        focusMarkerId={focusMarkerId}
        etaOverlay={etaOverlay}
        apiKey={apiKey}
        className={className}
      />
    );
  }

  const tileUrl = order.mapProvider.kind === "osm" ? order.mapProvider.tileUrl : undefined;
  return (
    <OsmMapAdapter
      markers={markers}
      cameraMarkers={cameraMarkers}
      focusMarkerId={focusMarkerId}
      etaOverlay={etaOverlay}
      tileUrl={tileUrl}
      className={className}
    />
  );
};
